import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useUser } from '@/contexts/UserContext';
import { api, apiRequest } from '@/config/api';
import { SessionManager } from '@/utils/sessionManager';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Calendar,
  MapPin,
  Users,
  Save,
  ArrowLeft,
  Upload,
  Image as ImageIcon,
  X,
  Edit,
  Plus,
  AlertCircle
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface EventFormData {
  title: string;
  description: string;
  date: string;
  location: string;
  category: string;
  maxparticipants: string;
  status: string;
  // Campos de monetização
  price: string;
  tags: string;
  monetizationtype: string;
  platformcommissionrate: string;
  photographercommissionrate: string;
  maxphotographers: string;
  priceperphoto: string;
  isopenforregistration: boolean;
  isopenforpublic: boolean;
}

import { EventCombobox, type EventOption } from '@/components/ui/event-combobox';

export default function EventForm() {
  const { user, isLoading } = useUser();
  const [, setLocation] = useLocation();
  const [loading, setLoading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [originalCoverImage, setOriginalCoverImage] = useState<string | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [eventOwnerName, setEventOwnerName] = useState<string>('');

  // Verificar se é edição
  const isEdit = window.location.pathname.includes('/edit');
  const eventId = isEdit ? window.location.pathname.split('/')[2] : null;

  // Estado para o fluxo de criação (search -> create)
  const [step, setStep] = useState<'search' | 'create' | 'edit'>(isEdit ? 'edit' : 'search');
  const [searchResults, setSearchResults] = useState<EventOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [similarEvents, setSimilarEvents] = useState<any[]>([]);
  const [isCheckingSimilar, setIsCheckingSimilar] = useState(false);
  const [showSimilarEvents, setShowSimilarEvents] = useState(false);

  useEffect(() => {
    if (user) {
      const userEmail = user.email?.toLowerCase() || '';
      const adminEmails = ['admin@fotup.com', 'admin@devonno.com'];
      setIsAdmin(adminEmails.includes(userEmail));
    }
  }, [user]);

  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    description: '',
    date: '',
    location: '',
    category: '',
    maxparticipants: '',
    status: 'PendingApproval', // Default para novos eventos
    // Campos de monetização
    price: '',
    tags: '',
    monetizationtype: 'sale',
    platformcommissionrate: '0.10',
    photographercommissionrate: '0.70',
    maxphotographers: '10',
    priceperphoto: '',
    isopenforregistration: true,
    isopenforpublic: true
  });

  useEffect(() => {
    if (!isLoading && !user) {
      setLocation('/login');
    }
  }, [isLoading, user, setLocation]);

  // Verificar se a sessão foi perdida durante o uso
  useEffect(() => {
    const checkSession = () => {
      const token = SessionManager.getToken();
      if (!token && user) {
        console.warn('⚠️ Sessão perdida durante o uso - redirecionando para login');
        toast({
          title: 'Sessão expirada',
          description: 'Sua sessão expirou. Por favor, faça login novamente.',
          variant: 'destructive',
        });
        setLocation('/login');
      }
    };

    // Verificar a cada 30 segundos
    const interval = setInterval(checkSession, 30000);

    return () => clearInterval(interval);
  }, [user, setLocation]);

  useEffect(() => {
    if (isEdit && eventId) {
      fetchEventData();
    }
  }, [isEdit, eventId]);

  const fetchEventData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/events/${eventId}`);

      if (response.data.success) {
        const event = response.data.data;
        
        // Verificar se o usuário é dono do evento
        const userIsOwner = event.createdbyuserid === user?.id;
        setIsOwner(userIsOwner);

        // Buscar nome do criador do evento (já vem do backend como ownerName)
        setEventOwnerName(event.ownerName || 'Usuário');

        // Verificar se o usuário está inscrito no evento
        if (!userIsOwner && user?.id) {
          try {
            const subscriptionResponse = await api.get(`/api/events/${eventId}/subscription`);
            if (subscriptionResponse.data.success && subscriptionResponse.data.data) {
              setIsSubscribed(true);
              setSubscriptionStatus(subscriptionResponse.data.data.status);
            } else {
              setIsSubscribed(false);
              setSubscriptionStatus(null);
            }
          } catch (error) {
            console.log('Usuário não está inscrito no evento');
            setIsSubscribed(false);
            setSubscriptionStatus(null);
          }
        } else {
          setIsSubscribed(false);
          setSubscriptionStatus(null);
        }

        setFormData({
          title: event.title || '',
          description: event.description || '',
          date: event.date ? new Date(event.date).toISOString().slice(0, 16) : '',
          location: event.location || '',
          category: event.category || '',
          maxparticipants: event.maxparticipants?.toString() || '',
          status: event.status || 'Draft',
          // Campos de monetização
          price: event.price?.toString() || '',
          tags: event.tags || '',
          monetizationtype: event.monetizationtype || 'sale',
          platformcommissionrate: event.platformcommissionrate?.toString() || '0.10',
          photographercommissionrate: event.photographercommissionrate?.toString() || '0.70',
          maxphotographers: event.maxphotographers?.toString() || '10',
          priceperphoto: event.priceperphoto?.toString() || '',
          isopenforregistration: event.isopenforregistration ?? true,
          isopenforpublic: event.isopenforpublic ?? true
        });

        if (event.coverimage) {
          setCoverImage(event.coverimage);
          setOriginalCoverImage(event.coverimage);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar evento:', error);
      toast({
        title: 'Erro ao carregar evento',
        description: 'Não foi possível carregar os dados do evento.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const checkSimilarEvents = async (title: string) => {
    if (!title || title.trim().length < 3) {
      setSimilarEvents([]);
      setShowSimilarEvents(false);
      return;
    }

    try {
      setIsCheckingSimilar(true);
      const response = await api.get(`/api/events/similar?title=${encodeURIComponent(title)}&limit=5`);
      if (response.data.success && response.data.data.length > 0) {
        setSimilarEvents(response.data.data);
        setShowSimilarEvents(true);
      } else {
        setSimilarEvents([]);
        setShowSimilarEvents(false);
      }
    } catch (error) {
      console.error('Erro ao verificar eventos similares:', error);
      setSimilarEvents([]);
      setShowSimilarEvents(false);
    } finally {
      setIsCheckingSimilar(false);
    }
  };

  // Debounce para verificar eventos similares
  useEffect(() => {
    if (step === 'create' && formData.title && formData.title.trim().length >= 3) {
      const timeoutId = setTimeout(() => {
        checkSimilarEvents(formData.title);
      }, 500);
      
      return () => clearTimeout(timeoutId);
    } else {
      setSimilarEvents([]);
      setShowSimilarEvents(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.title, step]);

  const handleInputChange = (field: keyof EventFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCoverImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCoverFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setCoverImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCoverImage = () => {
    setCoverImage(null);
    setCoverFile(null);
  };

  const uploadCoverImage = async (file: File): Promise<string | null> => {
    try {
      const token = SessionManager.getToken();

      // Verificar se o token existe
      if (!token) {
        console.error('❌ Token não encontrado - redirecionando para login');
        toast({
          title: 'Sessão expirada',
          description: 'Sua sessão expirou. Por favor, faça login novamente.',
          variant: 'destructive',
        });
        setLocation('/login');
        return null;
      }

      // Se há uma imagem anterior, excluir primeiro
      if (originalCoverImage) {
        console.log('🗑️ Excluindo imagem anterior:', originalCoverImage);
        try {
          await apiRequest('/api/events/upload/cover', {
            method: 'DELETE',
            body: JSON.stringify({ url: originalCoverImage })
          });
          console.log('✅ Imagem anterior excluída com sucesso');
        } catch (error) {
          console.warn('⚠️ Erro ao excluir imagem anterior (pode não existir):', error);
          // Não falhar o upload por causa da exclusão
        }
      }

      const formData = new FormData();
      formData.append('file', file);

      console.log('📤 Fazendo upload da nova imagem de capa...');
      console.log('🔑 Token disponível:', !!token);

      // Usar apiRequest diretamente para upload de arquivos
      const response = await apiRequest('/api/events/upload/cover', {
        method: 'POST',
        body: formData,
        headers: {
          // Não definir Content-Type para multipart/form-data
          // O browser define automaticamente com boundary
          // Manter apenas Authorization
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      console.log('📤 Resposta do upload:', data);

      if (data.success) {
        return data.data.url;
      }
      return null;
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);

      // Se for erro 401, redirecionar para login
      if (error instanceof Error && error.message && error.message.includes('401')) {
        console.error('❌ Token inválido - redirecionando para login');
        toast({
          title: 'Sessão expirada',
          description: 'Sua sessão expirou. Por favor, faça login novamente.',
          variant: 'destructive',
        });
        setLocation('/login');
      }

      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Verificar se o usuário está autenticado
    if (!user?.id) {
      console.error('❌ Usuário não autenticado no handleSubmit');
      toast({
        title: 'Usuário não autenticado',
        description: 'Você precisa estar logado para criar eventos.',
        variant: 'destructive',
      });
      setLocation('/login');
      return;
    }

    // Se for edição e não for dono, bloquear
    if (isEdit && !isOwner) {
      toast({
        title: 'Sem permissão',
        description: 'Apenas o proprietário do evento pode editá-lo.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.title.trim()) {
      toast({
        title: 'Título obrigatório',
        description: 'Por favor, informe o título do evento.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.date) {
      toast({
        title: 'Data obrigatória',
        description: 'Por favor, informe a data do evento.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);

      let coverImageUrl = coverImage;

      // Upload da imagem de capa se houver arquivo novo
      if (coverFile) {
        setUploadingCover(true);
        const uploadedUrl = await uploadCoverImage(coverFile);
        if (uploadedUrl) {
          coverImageUrl = uploadedUrl;
        } else {
          toast({
            title: 'Erro no upload da imagem',
            description: 'Não foi possível fazer upload da imagem de capa.',
            variant: 'destructive',
          });
          return;
        }
        setUploadingCover(false);
      }

      const eventData = {
        title: formData.title,
        description: formData.description || null,
        date: formData.date ? new Date(formData.date).toISOString() : formData.date,
        location: formData.location || null,
        category: formData.category || null,
        status: formData.status || 'Draft',
        maxParticipants: formData.maxparticipants ? parseInt(formData.maxparticipants) : undefined,
        coverImage: coverImageUrl,
        // Campos de monetização
        price: formData.price ? parseFloat(formData.price) : undefined,
        tags: formData.tags || undefined,
        monetizationType: formData.monetizationtype || 'sale',
        platformCommissionRate: formData.platformcommissionrate ? parseFloat(formData.platformcommissionrate) : 0.10,
        photographerCommissionRate: formData.photographercommissionrate ? parseFloat(formData.photographercommissionrate) : 0.70,
        maxPhotographers: formData.maxphotographers ? parseInt(formData.maxphotographers) : 10,
        pricePerPhoto: formData.priceperphoto ? parseFloat(formData.priceperphoto) : undefined,
        isOpenForRegistration: formData.isopenforregistration,
        isOpenForPublic: formData.isopenforpublic
      };

      // Log detalhado para debug
      console.log('🔍 DEBUG - FormData original:', formData);
      console.log('🔍 DEBUG - EventData construído:', eventData);
      console.log('🔍 DEBUG - Campos booleanos:', {
        isOpenForRegistration: eventData.isOpenForRegistration,
        isOpenForPublic: eventData.isOpenForPublic,
        originalValues: {
          isopenforregistration: formData.isopenforregistration,
          isopenforpublic: formData.isopenforpublic
        }
      });

      // Log específico para campos problemáticos
      console.log('🔍 DEBUG - Campos específicos:', {
        status: { value: eventData.status, type: typeof eventData.status },
        maxParticipants: { value: eventData.maxParticipants, type: typeof eventData.maxParticipants },
        platformCommissionRate: { value: eventData.platformCommissionRate, type: typeof eventData.platformCommissionRate },
        photographerCommissionRate: { value: eventData.photographerCommissionRate, type: typeof eventData.photographerCommissionRate },
        maxPhotographers: { value: eventData.maxPhotographers, type: typeof eventData.maxPhotographers },
        isOpenForRegistration: { value: eventData.isOpenForRegistration, type: typeof eventData.isOpenForRegistration },
        isOpenForPublic: { value: eventData.isOpenForPublic, type: typeof eventData.isOpenForPublic }
      });


      let response;
      if (isEdit) {
        response = await api.put(`/api/events/${eventId}`, eventData);
      } else {
        console.log('➕ Criando novo evento');
        response = await api.post('/api/events', eventData);
      }


      if (response.data.success) {
        toast({
          title: isEdit ? 'Evento atualizado' : 'Evento criado',
          description: isEdit
            ? 'O evento foi atualizado com sucesso.'
            : 'O evento foi criado com sucesso.',
        });

        // Se estiver editando, sair do modo de edição
        if (isEdit && isEditing) {
          setIsEditing(false);
        } else {
          setLocation('/events/management');
        }
      }
    } catch (error) {
      console.error('Erro ao salvar evento:', error);
      toast({
        title: 'Erro ao salvar evento',
        description: 'Não foi possível salvar o evento.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setUploadingCover(false);
    }
  };

  const handleSearchEvent = async (query: string) => {
    if (!query || query.length < 3) return;
    
    try {
      setIsSearching(true);
      const response = await api.get(`/api/events?search=${encodeURIComponent(query)}&limit=10`);
      
      if (response.data.success) {
        const options = response.data.data.map((event: any) => ({
          id: event.id,
          title: event.title,
          date: event.date,
          location: event.location,
          status: event.status
        }));
        setSearchResults(options);
      }
    } catch (error) {
      console.error('Erro ao buscar eventos:', error);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (selectedEventId) {
      checkSubscription(selectedEventId);
    } else {
      setSubscriptionStatus(null);
    }
  }, [selectedEventId]);

  const checkSubscription = async (eventId: string) => {
    try {
      const response = await api.get(`/api/events/${eventId}/subscription`);
      if (response.data.success && response.data.isSubscribed) {
        setSubscriptionStatus(response.data.data.status);
      } else {
        setSubscriptionStatus(null);
      }
    } catch (error) {
      console.error('Erro ao verificar inscrição:', error);
    }
  };

  const handleSubscribe = async () => {
    if (!selectedEventId) return;
    
    try {
      const response = await api.post(`/api/events/${selectedEventId}/subscribe`);
      if (response.data.success) {
        toast({
          title: 'Inscrição realizada!',
          description: 'Sua solicitação foi enviada para aprovação.',
        });
        checkSubscription(selectedEventId);
      }
    } catch (error) {
      console.error('Erro ao inscrever-se:', error);
      toast({
        title: 'Erro na inscrição',
        description: 'Não foi possível realizar a inscrição.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {isEdit ? 'Carregando evento...' : 'Carregando...'}
          </p>
        </div>
      </div>
    );
  }

  // Renderização do passo de busca
  if (step === 'search') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col items-center justify-center p-4">
        <Card className="w-full max-w-lg shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Calendar className="w-6 h-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">Criar Novo Evento</CardTitle>
            <p className="text-muted-foreground mt-2">
              Antes de criar, verifique se o evento já existe.
            </p>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <div className="space-y-2">
              <Label>Buscar Evento</Label>
              <EventCombobox
                options={searchResults}
                onSearch={handleSearchEvent}
                isLoading={isSearching}
                placeholder="Digite o nome do evento..."
                emptyText="Nenhum evento encontrado. Crie um novo!"
                value={selectedEventId}
                onValueChange={setSelectedEventId}
                onCreateNew={() => setStep('create')}
                renderOption={(option) => (
                  <div className="flex flex-col gap-1">
                    <span className="font-medium">{option.title}</span>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{new Date(option.date!).toLocaleDateString('pt-BR')}</span>
                      {option.location && <span>• {option.location}</span>}
                    </div>
                  </div>
                )}
              />
            </div>

            {selectedEventId ? (
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-medium text-slate-900 mb-2">Evento Encontrado!</h4>
                <p className="text-sm text-slate-600 mb-4">
                  Este evento já está cadastrado em nossa plataforma.
                </p>
                
                {subscriptionStatus ? (
                  <div className={`p-3 rounded-md text-sm font-medium mb-4 ${
                    subscriptionStatus === 'Approved' ? 'bg-green-100 text-green-800' :
                    subscriptionStatus === 'Rejected' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {subscriptionStatus === 'Approved' ? 'Você já está inscrito neste evento.' :
                     subscriptionStatus === 'Rejected' ? 'Sua inscrição foi rejeitada.' :
                     'Sua inscrição está pendente de aprovação.'}
                  </div>
                ) : (
                  <Button 
                    className="w-full" 
                    onClick={handleSubscribe}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Solicitar Inscrição
                  </Button>
                )}
              </div>
            ) : (
              <div className="pt-4 border-t">
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => {
                    // Se for admin, pode definir status como Approved direto, senão PendingApproval
                    if (isAdmin) {
                      setFormData(prev => ({ ...prev, status: 'Approved' }));
                    }
                    setStep('create');
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Novo Evento
                </Button>
              </div>
            )}
            
            <div className="text-center">
               <Button variant="link" onClick={() => setLocation('/events/management')}>
                 Voltar para Gestão
               </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto px-4 py-6">
        {/* Input oculto para upload de capa - sempre disponível */}
        <input
          id="cover-upload"
          type="file"
          accept="image/*"
          onChange={handleCoverImageChange}
          className="hidden"
        />
        {/* Header Moderno */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
          </div>
        </div>

        {/* Quadro Principal de Visualização - APENAS LEITURA */}
        {isEdit && (
          <Card className="bg-white/90 backdrop-blur-sm border-slate-200 shadow-lg mb-6">
            <CardContent className="p-6">
              <div className="flex gap-6">
                {/* Imagem de Capa */}
                <div className="relative flex-shrink-0">
                  {coverImage ? (
                    <div className="relative group">
                      <img
                        src={coverImage}
                        alt="Imagem de capa"
                        className="w-32 h-32 object-cover rounded-lg border-2 border-slate-200"
                      />
                      {isEditing && (
                        <button
                          type="button"
                          onClick={() => {
                            // Criar input temporário
                            const tempInput = document.createElement('input');
                            tempInput.type = 'file';
                            tempInput.accept = 'image/*';
                            tempInput.style.position = 'absolute';
                            tempInput.style.left = '-9999px';
                            tempInput.style.opacity = '0';

                            tempInput.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) {
                                setCoverFile(file);
                                const reader = new FileReader();
                                reader.onload = (e) => {
                                  setCoverImage(e.target?.result as string);
                                };
                                reader.readAsDataURL(file);
                              }
                              document.body.removeChild(tempInput);
                            };

                            document.body.appendChild(tempInput);
                            tempInput.click();
                          }}
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center cursor-pointer"
                        >
                          <Upload className="h-6 w-6 text-white" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div
                      className={`w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center transition-colors ${isEditing
                        ? 'border-slate-300 cursor-pointer hover:border-slate-400'
                        : 'border-slate-200 cursor-not-allowed'
                        }`}
                      onClick={() => {
                        if (!isEditing) {
                          toast({
                            title: 'Modo de edição necessário',
                            description: 'Clique em "Editar" para alterar a imagem de capa.',
                            variant: 'destructive',
                          });
                          return;
                        }

                        // Criar input temporário
                        const tempInput = document.createElement('input');
                        tempInput.type = 'file';
                        tempInput.accept = 'image/*';
                        tempInput.style.position = 'absolute';
                        tempInput.style.left = '-9999px';
                        tempInput.style.opacity = '0';

                        tempInput.onchange = (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (file) {
                            setCoverFile(file);
                            const reader = new FileReader();
                            reader.onload = (e) => {
                              setCoverImage(e.target?.result as string);
                            };
                            reader.readAsDataURL(file);
                          }
                          document.body.removeChild(tempInput);
                        };

                        document.body.appendChild(tempInput);
                        tempInput.click();
                      }}
                    >
                      <ImageIcon className={`h-8 w-8 ${isEditing ? 'text-slate-400' : 'text-slate-300'}`} />
                    </div>
                  )}
                </div>

                {/* Informações do Evento */}
                <div className="flex-1">
                  <div className="mb-4">
                    <h1 className="text-2xl font-bold text-slate-800 mb-2">
                      {formData.title || 'Nome do Evento'}
                    </h1>
                    <p className="text-slate-600 text-sm">
                      {formData.description || 'Descrição do evento'}
                    </p>
                  </div>

                  {/* Informações de Auditoria */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Autor</Label>
                      <p className="text-sm font-medium text-slate-800">{isEdit ? (eventOwnerName || 'Usuário') : (user?.name || 'Usuário')}</p>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Data de Cadastro</Label>
                      <p className="text-sm font-medium text-slate-800">{new Date().toLocaleDateString('pt-BR')}</p>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Status</Label>
                      <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        {formData.status}
                      </div>
                    </div>
                  </div>


                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Indicador de Status */}
        {isEdit && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 flex-wrap">
                {isOwner ? (
                  <>
                    <Badge variant="default" className="text-sm px-3 py-1">
                      <Users className="h-4 w-4 mr-2" />
                      Proprietário do Evento
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      Você pode editar todas as informações do evento
                    </span>
                  </>
                ) : isSubscribed ? (
                  <>
                    <Badge variant="secondary" className="text-sm px-3 py-1">
                      <Users className="h-4 w-4 mr-2" />
                      Fotógrafo Inscrito
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      Status: {subscriptionStatus === 'Approved' ? 'Aprovado' : subscriptionStatus === 'Pending' ? 'Pendente' : subscriptionStatus}
                    </span>
                    <span className="text-sm text-muted-foreground ml-auto">
                      Você pode apenas adicionar fotos ao evento
                    </span>
                  </>
                ) : (
                  <>
                    <Badge variant="outline" className="text-sm px-3 py-1">
                      Visualizador
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      Você não tem permissão para editar este evento
                    </span>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <form id="event-form" onSubmit={handleSubmit} className="space-y-6">
          {/* Botões de Ação */}
          <div className="flex gap-3 flex-wrap justify-start mb-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setLocation('/events/management')}
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>

            {(isOwner || isSubscribed) && (
              <Button
                type="button"
                onClick={() => setLocation(`/uploader/dashboard?eventId=${eventId}`)}
                className="bg-purple-600 hover:bg-purple-700 text-white font-medium"
              >
                <Upload className="h-4 w-4 mr-2" />
                Adicionar Fotos
              </Button>
            )}

            {isEdit && isOwner && (
              <>
                {!isEditing ? (
                  <Button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="bg-primary hover:bg-secondary text-primary-foreground font-medium"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Editar
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsEditing(false)}
                    className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    Cancelar
                  </Button>
                )}
              </>
            )}

            {!isEdit ? (
              <Button
                type="submit"
                disabled={loading || uploadingCover}
                className="bg-primary hover:bg-secondary text-primary-foreground font-medium"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Criando...' : 'Criar Evento'}
              </Button>
            ) : (
              isEditing && isOwner && (
                <Button
                  type="submit"
                  disabled={loading || uploadingCover}
                  className="bg-primary hover:bg-secondary text-primary-foreground font-medium"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? 'Atualizando...' : 'Atualizar Evento'}
                </Button>
              )
            )}
          </div>
          {/* Formulários Editáveis - Dados do Evento e Monetização lado a lado */}
          <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
            {/* Dados do Evento */}
            <Card className="bg-white/80 backdrop-blur-sm border-slate-200 shadow-sm lg:col-span-7">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-slate-800 flex items-center">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                  Dados do Evento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Linha 1: Título, Categoria, Local, Data */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="relative">
                    <Label htmlFor="title" className="text-sm font-medium text-slate-700">Título do Evento *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="Ex: Maratona de São Paulo 2025"
                      required
                      className="bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500 mt-1"
                      disabled={(!isEditing && isEdit) || (!isOwner && isEdit)}
                    />
                    {showSimilarEvents && similarEvents.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border-2 border-yellow-300 rounded-md shadow-xl max-h-60 overflow-y-auto">
                        <div className="p-2 border-b bg-yellow-50">
                          <p className="text-xs font-semibold text-yellow-800 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Eventos similares encontrados:
                          </p>
                        </div>
                        <div className="divide-y">
                          {similarEvents.map((event) => (
                            <div
                              key={event.id}
                              className="p-3 hover:bg-slate-50 cursor-pointer transition-colors"
                              onClick={() => {
                                setLocation(`/event/${event.id}`);
                              }}
                            >
                              <div className="font-medium text-sm">{event.title}</div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                {event.date && (
                                  <span>{new Date(event.date).toLocaleDateString('pt-BR')}</span>
                                )}
                                {event.location && <span>• {event.location}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="p-2 border-t bg-slate-50">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowSimilarEvents(false);
                            }}
                          >
                            Continuar criando mesmo assim
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="category" className="text-sm font-medium text-slate-700">Categoria</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => handleInputChange('category', value)}
                      disabled={(!isEditing && isEdit) || (!isOwner && isEdit)}
                    >
                      <SelectTrigger className="bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500 mt-1">
                        <SelectValue placeholder="Selecione uma categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Esporte">Esporte</SelectItem>
                        <SelectItem value="Corrida">Corrida</SelectItem>
                        <SelectItem value="Ciclismo">Ciclismo</SelectItem>
                        <SelectItem value="Natação">Natação</SelectItem>
                        <SelectItem value="Triathlon">Triathlon</SelectItem>
                        <SelectItem value="Outros">Outros</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="location" className="text-sm font-medium text-slate-700">Local</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      placeholder="Ex: Parque Ibirapuera, São Paulo"
                      className="bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500 mt-1"
                      disabled={(!isEditing && isEdit) || (!isOwner && isEdit)}
                    />
                  </div>

                  <div>
                    <Label htmlFor="date" className="text-sm font-medium text-slate-700">Data *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date ? formData.date.split('T')[0] : ''}
                      onChange={(e) => handleInputChange('date', e.target.value + 'T00:00')}
                      required
                      className="bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500 mt-1"
                      disabled={(!isEditing && isEdit) || (!isOwner && isEdit)}
                    />
                  </div>
                </div>

                {/* Linha 2: Descrição */}
                <div>
                  <Label htmlFor="description" className="text-sm font-medium text-slate-700">Descrição</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Descreva o evento..."
                    rows={4}
                    className="bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500 mt-1"
                    disabled={(!isEditing && isEdit) || (!isOwner && isEdit)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Configurações de Monetização */}
            <Card className="bg-white/80 backdrop-blur-sm border-slate-200 shadow-sm lg:col-span-3">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-slate-800 flex items-center">
                  <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                  Configurações de Monetização
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Linha 1: Preço, Tipo, Tags */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="price" className="text-xs font-medium text-slate-600">Preço (R$)</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', e.target.value)}
                      placeholder="0.00"
                      className="bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500 mt-1"
                      disabled={(!isEditing && isEdit) || (!isOwner && isEdit)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="monetizationtype" className="text-xs font-medium text-slate-600">Tipo</Label>
                    <Select
                      value={formData.monetizationtype}
                      onValueChange={(value) => handleInputChange('monetizationtype', value)}
                      disabled={(!isEditing && isEdit) || (!isOwner && isEdit)}
                    >
                      <SelectTrigger className="bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sale">Venda</SelectItem>
                        <SelectItem value="free">Gratuito</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="tags" className="text-xs font-medium text-slate-600">Tags</Label>
                    <Input
                      id="tags"
                      value={formData.tags}
                      onChange={(e) => handleInputChange('tags', e.target.value)}
                      placeholder="corrida, maratona"
                      className="bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500 mt-1"
                      disabled={(!isEditing && isEdit) || (!isOwner && isEdit)}
                    />
                  </div>
                </div>

                {/* Linha 2: Taxas */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="platformcommissionrate" className="text-xs font-medium text-slate-600">Taxa Plataforma (%)</Label>
                    <Input
                      id="platformcommissionrate"
                      type="number"
                      step="0.01"
                      value={formData.platformcommissionrate}
                      onChange={(e) => handleInputChange('platformcommissionrate', e.target.value)}
                      placeholder="10.00"
                      className="bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500 mt-1"
                      disabled={(!isEditing && isEdit) || (!isOwner && isEdit)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="photographercommissionrate" className="text-xs font-medium text-slate-600">Taxa Fotógrafo (%)</Label>
                    <Input
                      id="photographercommissionrate"
                      type="number"
                      step="0.01"
                      value={formData.photographercommissionrate}
                      onChange={(e) => handleInputChange('photographercommissionrate', e.target.value)}
                      placeholder="70.00"
                      className="bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500 mt-1"
                      disabled={(!isEditing && isEdit) || (!isOwner && isEdit)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="maxphotographers" className="text-xs font-medium text-slate-600">Máx Fotógrafos</Label>
                    <Input
                      id="maxphotographers"
                      type="number"
                      value={formData.maxphotographers}
                      onChange={(e) => handleInputChange('maxphotographers', e.target.value)}
                      placeholder="10"
                      className="bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500 mt-1"
                      disabled={(!isEditing && isEdit) || (!isOwner && isEdit)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Campo oculto para imagem de capa */}
          <input
            type="hidden"
            name="coverImage"
            value={coverImage || ''}
          />



        </form>
      </div>
    </div>
  );
}
